/**
 * Composant Dashboard
 *
 * Composant principal du dashboard qui orchestre tous les autres composants.
 * Gère :
 * - Le chargement des données Odoo
 * - L'auto-refresh toutes les 5 minutes
 * - L'affichage des différentes sections (Header, Stats, UserStories, Epics, Milestones)
 * - La modal des détails des User Stories
 * - L'export des données (PDF structuré, Excel, CSV)
 *
 * @param {number} selectedProjectId - ID du projet sélectionné (optionnel)
 * @param {Array} projects - Liste des projets disponibles (optionnel)
 * @param {Function} onProjectChange - Callback lors du changement de projet (optionnel)
 * @param {Function} onLogout - Callback pour la déconnexion (optionnel)
 */

import React, { useState } from 'react';
import { useProjectData } from '../../context/ProjectDataContext';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import * as exportService from '../../services/exportService';

// Composants
import Header from './Header';
import HorizonOverview from './HorizonOverview';
import '../../styles/horizonHome.css';
import MilestonesTimeline from './MilestonesTimeline';
import ExportPDFModal from '../modals/ExportPDFModal';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorMessage from '../common/ErrorMessage';

const Dashboard = ({
  selectedProjectId,
  projects = [],
  onProjectChange,
  onLogout,
}) => {

  // États locaux
  const [projectId, setProjectId] = useState(selectedProjectId || null);
  const [showExportPDFModal, setShowExportPDFModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Lire les données depuis le contexte partagé (pas de nouvel appel API)
  const {
    userStories,
    epics,
    milestones,
    loading,
    error,
    refresh,
  } = useProjectData();

  // Auto-refresh toutes les 5 minutes
  const { formatLastRefreshTime, refreshNow } = useAutoRefresh(refresh, 5 * 60 * 1000, { refreshOnVisible: false });

  // Trouver les informations du projet
  const currentProject = projects.find(p => p.id === projectId) || {
    name: 'Dashboard Odoo',
    clientName: 'Votre organisation',
  };

  /**
   * Gérer le changement de projet
   */
  const handleProjectChange = (newProjectId) => {
    setProjectId(newProjectId);
    if (onProjectChange) {
      onProjectChange(newProjectId);
    }
  };

  /**
   * Gérer l'export
   */
  const handleExport = async (type) => {
    if (isExporting) {
      return; // Empêcher les exports multiples simultanés
    }

    try {
      switch (type) {
        case 'pdf':
          // Ouvrir le modal de personnalisation au lieu d'exporter directement
          setShowExportPDFModal(true);
          break;
        case 'excel':
          await exportToExcel();
          break;
        case 'csv':
          await exportToCSV();
          break;
        default:
          console.warn('Type d\'export non supporté:', type);
      }
    } catch (err) {
      console.error('Erreur lors de l\'export:', err);
    }
  };

  /**
   * Exporter le dashboard en PDF (version complète - gardée pour compatibilité)
   */
  // eslint-disable-next-line no-unused-vars
  const exportToPDF = async () => {
    setIsExporting(true);
    try {
      await exportService.exportToPDF(
        userStories,
        epics,
        milestones,
        currentProject.clientName || 'Votre organisation',
        currentProject.name || 'Projet'
      );
      alert('Export PDF réussi !');
    } catch (error) {
      console.error('Erreur export PDF:', error);
      alert(error.message || 'Erreur lors de l\'export PDF. Veuillez réessayer.');
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Filtrer les données selon les sélections du modal
   */
  const filterDataForExport = (userStories, epics, milestones, selections) => {
    // Filtrer les User Stories selon catégories et sélection individuelle
    let filteredUS = [];
    if (selections.includeSections.userStories) {
      filteredUS = userStories.filter(us => {
        const categorySelected = selections.categories[us.category];
        const usSelected = selections.selectedUserStories.has(us.id);
        return categorySelected && usSelected;
      });
    }

    // Filtrer milestones et epics selon sections
    const filteredMilestones = selections.includeSections.milestones ? milestones : [];
    const filteredEpics = selections.includeSections.statistics ? epics : [];

    return {
      userStories: filteredUS,
      epics: filteredEpics,
      milestones: filteredMilestones
    };
  };

  /**
   * Exporter le PDF avec les sélections personnalisées
   */
  const handleExportPDFWithSelections = async (selections) => {
    setIsExporting(true);
    try {
      const filteredData = filterDataForExport(userStories, epics, milestones, selections);

      await exportService.exportToPDF(
        filteredData.userStories,
        filteredData.epics,
        filteredData.milestones,
        currentProject.clientName || 'Votre organisation',
        currentProject.name || 'Projet',
        selections
      );

      setShowExportPDFModal(false);
      alert('Export PDF réussi !');
    } catch (error) {
      console.error('Erreur export PDF:', error);
      alert(error.message || 'Erreur lors de l\'export PDF. Veuillez réessayer.');
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Exporter le dashboard en Excel
   */
  const exportToExcel = async () => {
    setIsExporting(true);
    try {
      await exportService.exportToExcel(
        userStories,
        epics,
        milestones,
        currentProject.name || 'Projet'
      );
      alert('Export Excel réussi !');
    } catch (error) {
      console.error('Erreur export Excel:', error);
      alert(error.message || 'Erreur lors de l\'export Excel. Veuillez réessayer.');
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Exporter le dashboard en CSV
   */
  const exportToCSV = async () => {
    setIsExporting(true);
    try {
      await exportService.exportToCSV(
        userStories,
        epics,
        milestones,
        currentProject.name || 'Projet'
      );
      alert('Export CSV réussi !');
    } catch (error) {
      console.error('Erreur export CSV:', error);
      alert(error.message || 'Erreur lors de l\'export CSV. Veuillez réessayer.');
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Gérer le rafraîchissement manuel
   */
  const handleRefresh = async () => {
    refreshNow();
  };

  // Afficher le spinner de chargement
  if (loading) {
    return (
      <div className="min-h-full bg-gray-50">
        <LoadingSpinner message="Chargement du dashboard..." />
      </div>
    );
  }

  // Afficher le message d'erreur
  if (error) {
    return (
      <div className="min-h-full bg-gray-50">
        <ErrorMessage message={error} onRetry={refresh} />
      </div>
    );
  }

  return (
    <div className="horizon-home">
      {/* Container principal */}
      <div className="horizon-home-inner">
        {/* En-tête */}
        <Header
          clientName={currentProject.clientName}
          projectName={currentProject.name}
          lastUpdateText={formatLastRefreshTime()}
          onRefresh={handleRefresh}
          onExport={handleExport}
          onLogout={onLogout}
          projects={projects}
          selectedProjectId={projectId}
          onProjectChange={handleProjectChange}
          isExporting={isExporting}
        />

        <div className="horizon-content">
          <HorizonOverview />
          <details className="horizon-calendar" open>
            <summary>Calendrier des jalons</summary>
            <MilestonesTimeline milestones={milestones} compact />
          </details>
        </div>
      </div>

      {/* Modal de personnalisation de l'export PDF */}
      <ExportPDFModal
        isOpen={showExportPDFModal}
        onClose={() => setShowExportPDFModal(false)}
        onExport={handleExportPDFWithSelections}
        userStories={userStories}
        epics={epics}
        milestones={milestones}
        clientName={currentProject.clientName || 'Votre organisation'}
        projectName={currentProject.name || 'Projet'}
        isGenerating={isExporting}
      />
    </div>
  );
};

export default Dashboard;
